if (!customElements.get('preload-links')) {
  class PreloadLinks extends HTMLElement {
    connectedCallback() {
      // Must be a standard page context
      if (this.closest('quick-add-drawer')) return;

      this.toggleAttribute('active', true);

      // Store cache on window for persistence
      if (!window.themePreloadLinksCache) window.themePreloadLinksCache = {};
      this.linksCache = window.themePreloadLinksCache;
      this.STATES = {
        FETCHING: 1,
        LOADWHENFETCHED: 2,
        FETCHED: 3
      };
      this.replacementTarget = '#main-content';
      this.cacheMax = 20;
      this.currentCacheIndex = 0;

      this.querySelectorAll('a').forEach((link) => {
        link.addEventListener('mouseenter', this.handleMouseEnter.bind(this), { once: true });
        link.addEventListener('focus', this.handleFocus.bind(this), { once: true });
      });
    }

    /**
     * Handles the mouse enter event on a link.
     * @param {object} evt - Event object.
     */
    handleMouseEnter(evt) {
      this.prepareLink(evt.currentTarget);
    }

    /**
     * Handles the focus event on a link.
     * @param {object} evt - Event object.
     */
    handleFocus(evt) {
      this.prepareLink(evt.currentTarget);
    }

    /**
     * Handles the click event on a link.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const link = evt.currentTarget;
      if (this.linksCache[link.href]) {
        evt.preventDefault();
        if (this.linksCache[link.href].state === this.STATES.FETCHED) {
          this.performLoad(link.href);
        } else {
          this.linksCache[link.href].state = this.STATES.LOADWHENFETCHED;
        }
      }
    }

    /**
     * Preload content and set up click handler.
     * @param {HTMLElement} link - Link to prepare.
     */
    prepareLink(link) {
      link.addEventListener('click', this.handleClick.bind(this), { once: true });
      this.preloadLink(link);
    }

    /**
     * Fetch page from a link and cache it.
     * @param {HTMLElement} link - Link to preload.
     */
    preloadLink(link) {
      if (!this.linksCache[link.href]) {
        // Basic cache limit
        if (Object.keys(this.linksCache).length > this.cacheMax) {
          this.removeOldestCache();
        }

        this.linksCache[link.href] = {
          state: this.STATES.FETCHING,
          id: ++this.currentCacheIndex // eslint-disable-line no-plusplus
        };
        fetch(link.href, { method: 'GET', mode: 'no-cors' })
          .then((response) => response.text())
          .then((html) => {
            this.linksCache[link.href].html = html;
            if (this.linksCache[link.href].state === this.STATES.LOADWHENFETCHED) {
              this.performLoad(link.href);
            }
            this.linksCache[link.href].state = this.STATES.FETCHED;
          })
          .catch(() => {}); // Ignore errors
      }
    }

    /**
     * Replaces the main content of the page.
     * @param {string} linkUrl - Link to load - used as a cache index.
     */
    performLoad(linkUrl) {
      const activeElementId = document.activeElement.id;
      const { scrollY } = window;

      const target = document.querySelector(this.replacementTarget);
      let tmpl = document.createElement('div');
      tmpl.innerHTML = this.linksCache[linkUrl].html;
      tmpl = tmpl.querySelector(this.replacementTarget);
      // Modify content before display
      // Remove animations
      tmpl.querySelectorAll('[data-cc-animate]').forEach((el) => el.removeAttribute('data-cc-animate'));
      // Preserve input values
      tmpl.querySelectorAll('input[id], select[id], textarea[id]').forEach((el) => {
        // Never carry over product-specific variant/form state between sibling product loads.
        if (el.closest('variant-picker') || el.closest('.product-options') || el.name === 'id') return;

        const existing = document.querySelector(`[id="${el.id}"]`);
        if (!existing || existing.type !== el.type || existing.tagName !== el.tagName) return;

        if (el.tagName === 'INPUT') {
          if (el.type === 'checkbox' || el.type === 'radio') {
            el.checked = existing.checked;
          } else {
            el.value = existing.value;
          }
        } else if (el.tagName === 'TEXTAREA') {
          el.value = existing.value;
        } else if (el.tagName === 'SELECT') {
          if (Array.from(el.options).some((opt) => opt.value === existing.value)) {
            el.value = existing.value;
          }
        }
      });

      target.replaceWith(tmpl);
      // Note: Scripts may not be run. This may be OK in practice.

      // Web components that need initialising again after changing document root
      document.querySelectorAll(`${this.replacementTarget} media-gallery`).forEach((e) => e.initGallery());

      window.scrollTo({ y: scrollY });
      window.history.replaceState({}, '', linkUrl);
      document.getElementById(activeElementId)?.focus();
    }

    /**
     * Delete the oldest item in the cache.
     */
    removeOldestCache() {
      let oldestId = Number.MAX_SAFE_INTEGER;
      for (const key in Object.keys(this.linksCache)) {
        if (this.linksCache[key].id < oldestId) oldestId = this.linksCache[key].id;
      }
      for (const key in Object.keys(this.linksCache)) {
        if (this.linksCache[key].id === oldestId) delete this.linksCache[key];
      }
    }
  }

  customElements.define('preload-links', PreloadLinks);
}
